import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import type { AiEvent } from "@/hooks/useAiAgent";

export interface AiPosterSelection {
  url: string;
  sha256: string;
}

type PickerProps = {
  event: Pick<AiEvent, "id" | "status" | "imageUrl" | "collectionMetadata">;
  selected: AiPosterSelection | null;
  onSelect: (poster: AiPosterSelection | null) => void;
  disabled?: boolean;
};

function PosterCard({
  eventId,
  url,
  index,
  selected,
  onSelect,
  disabled,
}: Omit<PickerProps, "event"> & { eventId: string; url: string; index: number }) {
  const [requested, setRequested] = useState(index === 0);
  const [loaded, setLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const preview = useQuery({
    queryKey: ["ai-poster-preview", eventId, url],
    enabled: requested,
    retry: false,
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const result = await api.get<{ dataUrl: string; sha256: string }>(
        `/admin/ai-agent/events/${eventId}/posters/preview`,
        { url },
      );
      if (!/^data:image\/(png|jpeg|webp);base64,/.test(result.dataUrl) || !result.sha256) {
        throw new Error("Niepoprawny podgląd plakatu");
      }
      return result;
    },
  });
  const error = preview.isError || imageError;
  const chosen = selected?.url === url;
  return (
    <div className="space-y-2 rounded-md border p-3">
      <p className="text-sm font-medium">Plakat {index + 1}</p>
      {preview.data && !imageError && (
        <img
          key={preview.dataUpdatedAt}
          src={preview.data.dataUrl}
          alt={`Podgląd plakatu ${index + 1}`}
          className="max-h-72 w-full rounded object-contain"
          onLoad={() => setLoaded(true)}
          onError={() => {
            setImageError(true);
            setLoaded(false);
            if (chosen) onSelect(null);
          }}
        />
      )}
      {requested && !error && !loaded && <p role="status">Ładowanie podglądu…</p>}
      {error && (
        <p role="alert">
          Nie udało się wyświetlić plakatu. Wybierz inny lub opublikuj bez plakatu.
        </p>
      )}
      {!requested && (
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          onClick={() => setRequested(true)}
        >
          Pokaż plakat {index + 1}
        </Button>
      )}
      {requested && (preview.data || error) && (
        <Button
          type="button"
          variant="outline"
          disabled={disabled || preview.isFetching}
          onClick={() => {
            if (chosen) onSelect(null);
            setLoaded(false);
            setImageError(false);
            void preview.refetch();
          }}
        >
          Odśwież podgląd {index + 1}
        </Button>
      )}
      <Button
        type="button"
        variant={chosen ? "default" : "outline"}
        aria-pressed={chosen}
        disabled={disabled || !loaded || error || preview.isFetching || !preview.data}
        onClick={() => onSelect(chosen ? null : { url, sha256: preview.data!.sha256 })}
      >
        {chosen ? `Wybrany plakat ${index + 1}` : `Wybierz plakat ${index + 1}`}
      </Button>
    </div>
  );
}

/** Mount with key={event.id} so requests and choices cannot leak between proposals. */
export function AiPosterPicker({ event, selected, onSelect, disabled }: PickerProps) {
  const [discovered, setDiscovered] = useState<string[]>([]);
  const discover = useMutation({
    mutationFn: () =>
      api.post<{ imageURLs: string[] }>(`/admin/ai-agent/events/${event.id}/posters/discover`, {}),
    onSuccess: (result) => setDiscovered(result.imageURLs),
  });
  const candidates = [
    ...new Set([
      ...(event.collectionMetadata?.imageURLs ?? []),
      ...(event.imageUrl ? [event.imageUrl] : []),
      ...discovered,
    ]),
  ].slice(0, 10);
  return (
    <section aria-label="Plakat wydarzenia" className="space-y-3 rounded-md border p-3">
      <h3 className="font-medium">Plakat wydarzenia</h3>
      <p className="text-sm text-muted-foreground">
        Sprawdź podgląd i wybierz jeden plakat do publikacji. Domyślnie wydarzenie zostanie
        opublikowane bez plakatu.
      </p>
      <Button
        type="button"
        variant={selected ? "outline" : "default"}
        aria-pressed={!selected}
        disabled={disabled}
        onClick={() => onSelect(null)}
      >
        Bez plakatu
      </Button>
      <div className="grid gap-3 sm:grid-cols-2">
        {candidates.map((url, index) => (
          <PosterCard
            key={url}
            eventId={event.id}
            url={url}
            index={index}
            selected={selected}
            onSelect={onSelect}
            disabled={disabled}
          />
        ))}
      </div>
      {candidates.length === 0 && <p>Brak zapisanych plakatów.</p>}
      <Button
        type="button"
        variant="outline"
        disabled={
          disabled ||
          discover.isPending ||
          event.status !== "pending_review" ||
          !event.collectionMetadata
        }
        onClick={() => discover.mutate()}
      >
        {discover.isPending ? "Szukanie obrazów…" : "Znajdź obrazy na stronie źródłowej"}
      </Button>
      {discover.isSuccess && discovered.length === 0 && (
        <p>Nie znaleziono obrazów na stronie źródłowej.</p>
      )}
      {discover.isError && (
        <p role="alert">Nie udało się pobrać obrazów ze źródła. Możesz ponowić wyszukiwanie.</p>
      )}
    </section>
  );
}
